import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS, apiClient } from '../config/api';
import { getFirebaseToken as getToken } from '../utils/getFirebaseToken';
import { getActiveRoleFromStorage } from '../utils/getActiveRole';
import { notesService } from '../services/notes.service';
import NoteCard from '../components/notes/NoteCard';
import { getNoteRoute } from '../components/notes/RiaNoteLinks';
import { LinkIcon, CalendarIcon } from '../components/notes/NoteTagIcons';
import '../components/notes/notes.css';
// Reuses the "Select Property" picker's list-card styling from the
// create-service-request page (csr-* classes only, all scoped — see that
// page's CSS, no bare tag selectors) so "Link to Transaction" here looks
// identical to that flow's transaction/property picker, per product ask.
import './CreateServiceRequestPage.css';

// Mirrors CreateServiceRequestPage.js's STATUS_BADGE map exactly (not
// exported there, so duplicated here rather than modifying that page).
const STATUS_BADGE = {
  PENDING:        { label: 'Prospect', cls: 'csr-badge-prospect' },
  ACTIVE:         { label: 'Active',   cls: 'csr-badge-active' },
  UNDER_CONTRACT: { label: 'Active',   cls: 'csr-badge-active' },
  CLOSING:        { label: 'Active',   cls: 'csr-badge-active' },
};

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

// Property field helpers — mirror FavoritesPage.js's flexible fallbacks
// exactly, since the single-property (mlsProperty) endpoint returns a
// flatter shape than the search endpoint's nested mls/address structure.
const getPropertyId = (prop) => (prop ? String(prop.mlsId || prop.mlsNumber || prop.listingId || prop.id || '') : '');
const getPropertyAddress = (prop) => (prop ? (prop.address || prop.propertyAddress || prop.fullAddress || prop.name || 'Unknown Address') : 'Unknown Address');
const getPropertyLocation = (prop) => {
  if (!prop) return '';
  const city = prop.city || prop.propertyCity || '';
  const state = prop.state || prop.propertyState || '';
  return [city, state].filter(Boolean).join(', ');
};
const getPropertyPrice = (prop) => {
  const price = prop?.listPrice || prop?.price || prop?.askingPrice || prop?.salePrice || 0;
  if (!price) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price);
};
const getPropertyDetails = (prop) => {
  const beds = prop?.bedrooms || prop?.beds || prop?.bedroomCount || 0;
  const baths = prop?.bathrooms || prop?.baths || prop?.bathroomCount || 0;
  const sqft = prop?.squareFeet || prop?.sqft || prop?.livingArea || prop?.area || 0;
  const parts = [];
  if (beds) parts.push(`${beds} bed`);
  if (baths) parts.push(`${baths} bath`);
  if (sqft) parts.push(`${Number(sqft).toLocaleString()} sqft`);
  return parts.join(' · ');
};
const FILTERS = [
  { key: 'ALL', label: 'All' },
  { key: 'NONE', label: 'Untagged' },
  { key: 'TRANSACTION', label: 'Transactions' },
  { key: 'PROPERTY', label: 'Properties' },
  { key: 'DOCUMENT', label: 'Documents' },
  { key: 'PARTY', label: 'Parties' },
  { key: 'CONVERSATION', label: 'Conversations' },
  { key: 'HAS_REMINDER', label: 'Has reminder' },
];

export default function MyNotesPage() {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('ALL');

  const [draftText, setDraftText] = useState('');
  const [draftReminder, setDraftReminder] = useState('');
  const [showReminderInput, setShowReminderInput] = useState(false);
  const [linkPicker, setLinkPicker] = useState(null); // 'TRANSACTION' | 'PROPERTY' | null
  const [selectedLink, setSelectedLink] = useState(null); // { linkType, linkId, linkLabel }
  const [transactionOptions, setTransactionOptions] = useState(null);
  const [propertyOptions, setPropertyOptions] = useState(null);
  const [txnSearch, setTxnSearch] = useState('');
  const [propSearch, setPropSearch] = useState('');
  const [saving, setSaving] = useState(false);

  // Self-heals notes whose `transactionName` snapshot was captured as the
  // literal fallback string "Transaction" (a UI placeholder, not a real
  // name) instead of the transaction's actual name/address — see
  // TransactionTimeline.js/TransactionDocuments.js/TransactionChatParties.js's
  // NotesPanel wiring, now fixed to never produce that string for new notes,
  // but notes created before that fix are stuck with it until repaired here.
  // Best-effort and silent: runs after the note list renders, never blocks
  // or surfaces errors, and only touches the denormalized display snapshot
  // (never the note's link) via notesService.repairTransactionName.
  const repairBadTransactionNames = useCallback(async (list, token) => {
    const broken = list.filter((n) => n.transactionName === 'Transaction' && n.transactionId);
    if (broken.length === 0) return;

    const uniqueTransactionIds = [...new Set(broken.map((n) => n.transactionId))];
    const realNames = {};
    await Promise.allSettled(uniqueTransactionIds.map(async (transactionId) => {
      try {
        const resp = await apiClient.get(API_ENDPOINTS.transactionById(transactionId), {
          headers: { Authorization: `Bearer ${token}` },
        });
        const txn = resp.data;
        const realName = txn?.transactionName || txn?.propertyAddress;
        if (realName) realNames[transactionId] = realName;
      } catch (e) {
        console.error('[MyNotesPage] Failed to resolve transaction for repair', transactionId, e);
      }
    }));

    const toRepair = broken.filter((n) => realNames[n.transactionId]);
    if (toRepair.length === 0) return;

    const results = await Promise.allSettled(
      toRepair.map((n) => notesService.repairTransactionName(token, n.id, realNames[n.transactionId])),
    );
    const repairedById = new Map();
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') repairedById.set(toRepair[i].id, r.value);
      else console.error('[MyNotesPage] Failed to repair note transactionName', toRepair[i].id, r.reason);
    });
    if (repairedById.size === 0) return;

    setNotes((prev) => prev.map((n) => repairedById.get(n.id) || n));
  }, []);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      if (!token) return;
      const list = await notesService.listNotes(token);
      setNotes(list);
      repairBadTransactionNames(list, token);
    } catch (e) {
      console.error('[MyNotesPage] Failed to load notes', e);
    } finally {
      setLoading(false);
    }
  }, [repairBadTransactionNames]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const filteredNotes = useMemo(() => {
    if (activeFilter === 'ALL') return notes;
    if (activeFilter === 'HAS_REMINDER') return notes.filter((n) => !!n.reminderDate);
    return notes.filter((n) => (n.linkType || 'NONE') === activeFilter);
  }, [notes, activeFilter]);

  const filteredTxnOptions = useMemo(() => {
    const list = transactionOptions || [];
    if (!txnSearch.trim()) return list;
    const q = txnSearch.toLowerCase();
    return list.filter((txn) =>
      (txn.propertyAddress || txn.transactionName || '').toLowerCase().includes(q) ||
      (txn.transactionNumber || '').toLowerCase().includes(q));
  }, [transactionOptions, txnSearch]);

  const filteredPropOptions = useMemo(() => {
    const list = propertyOptions || [];
    if (!propSearch.trim()) return list;
    const q = propSearch.toLowerCase();
    return list.filter((prop) => getPropertyAddress(prop).toLowerCase().includes(q));
  }, [propertyOptions, propSearch]);

  const loadTransactionOptions = async () => {
    if (transactionOptions) return;
    try {
      const token = await getToken();
      const viewingRole = getActiveRoleFromStorage();
      // size=100 (backend MAX_PAGE_SIZE), matching CreateServiceRequestPage's
      // picker, so older transactions aren't pushed off the first page.
      const url = `${API_ENDPOINTS.transactions}?size=100&sortBy=createdAt&sortOrder=desc${viewingRole ? `&viewingRole=${viewingRole}` : ''}`;
      const resp = await apiClient.get(url, { headers: { Authorization: `Bearer ${token}` } });
      const list = Array.isArray(resp.data) ? resp.data : (resp.data?.content || []);
      setTransactionOptions(list);
    } catch (e) {
      console.error('[MyNotesPage] Failed to load transactions', e);
      setTransactionOptions([]);
    }
  };

  // Favorites are stored as bare {favoriteId, propertyId, notes, createdAt} —
  // no address/name/photo. Mirrors FavoritesPage.js's two-step resolve:
  // fetch the favorited IDs, then hydrate each via the MLS-detail endpoint
  // in parallel. A stale/removed listing is silently dropped rather than
  // failing the whole picker.
  const loadPropertyOptions = async () => {
    if (propertyOptions) return;
    try {
      const token = await getToken();
      const idsResp = await apiClient.get(API_ENDPOINTS.favoritesIds, { headers: { Authorization: `Bearer ${token}` } });
      const ids = idsResp.data?.propertyIds ?? [];
      if (ids.length === 0) {
        setPropertyOptions([]);
        return;
      }
      const results = await Promise.allSettled(
        ids.map((id) => apiClient.get(API_ENDPOINTS.mlsProperty(id), { headers: { Authorization: `Bearer ${token}` } }))
      );
      const resolved = results
        .filter((r) => r.status === 'fulfilled')
        .map((r) => (Array.isArray(r.value.data) ? r.value.data[0] : r.value.data))
        .filter(Boolean);
      setPropertyOptions(resolved);
    } catch (e) {
      console.error('[MyNotesPage] Failed to load properties', e);
      setPropertyOptions([]);
    }
  };

  const openLinkPicker = (type) => {
    setLinkPicker((cur) => (cur === type ? null : type));
    setTxnSearch('');
    setPropSearch('');
    if (type === 'TRANSACTION') loadTransactionOptions();
    if (type === 'PROPERTY') loadPropertyOptions();
  };

  const pickTransaction = (txn) => {
    const address = txn.propertyAddress || txn.transactionName || 'Transaction';
    // Same property address can back multiple transactions (see the reference
    // picker) — append the transaction's own name/number to disambiguate.
    const disambiguator = txn.transactionName || txn.transactionNumber || '';
    setSelectedLink({
      linkType: 'TRANSACTION',
      linkId: txn.id || txn.transactionId,
      linkLabel: disambiguator && disambiguator !== address ? `${address} — ${disambiguator}` : address,
    });
    setLinkPicker(null);
  };

  const pickProperty = (prop) => {
    const address = getPropertyAddress(prop);
    const location = getPropertyLocation(prop);
    setSelectedLink({
      linkType: 'PROPERTY',
      linkId: getPropertyId(prop) || prop.propertyId,
      linkLabel: location ? `${address}, ${location}` : address,
    });
    setLinkPicker(null);
  };

  const clearLink = () => setSelectedLink(null);

  const handleSave = async () => {
    if (!draftText.trim() || saving) return;
    setSaving(true);
    try {
      const token = await getToken();
      const created = await notesService.createNote(token, {
        text: draftText.trim(),
        link: selectedLink || undefined,
        reminderDate: draftReminder || undefined,
        // Only the direct "Link to Transaction" picker sets linkId to a real
        // transaction ID (unlike the embedded panels on Timeline/Parties/
        // Documents, where linkId is a sub-entity ID) — captured so this note
        // is click-to-navigate consistent with those going forward. linkLabel
        // already shows the transaction identity here, so transactionName is
        // deliberately left unset to avoid a redundant pill.
        transactionId: selectedLink?.linkType === 'TRANSACTION' ? selectedLink.linkId : undefined,
      });
      setNotes((prev) => [created, ...prev]);
      setDraftText('');
      setDraftReminder('');
      setShowReminderInput(false);
      setSelectedLink(null);
    } catch (e) {
      console.error('[MyNotesPage] Failed to create note', e);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (noteId, patch) => {
    const token = await getToken();
    const updated = await notesService.updateNote(token, noteId, patch);
    setNotes((prev) => prev.map((n) => (n.id === noteId ? updated : n)));
  };

  const handleDelete = async (noteId) => {
    const token = await getToken();
    await notesService.deleteNote(token, noteId);
    setNotes((prev) => prev.filter((n) => n.id !== noteId));
  };

  // Jump to the section a note is about. Routing rules live in getNoteRoute
  // (RiaNoteLinks.js) so notes saved via Ria chat navigate identically.
  // transactionId is only populated for notes created after this feature
  // shipped; older notes without it are correctly left non-navigable.
  // CONVERSATION and untagged notes are not navigable here.
  const handleNoteClick = (note) => {
    const route = getNoteRoute(note);
    if (!route) return;
    if (route.state) navigate(route.path, { state: route.state });
    else navigate(route.path);
  };

  return (
    <div className="rn-page">
      <div className="rn-page-titlebar">
        <h1>My Notes</h1>
        <p>Private to you · optionally linked to a transaction, property, document, party, or conversation</p>
      </div>

      <div className="rn-top-composer">
        <textarea
          placeholder="Add a note…"
          value={draftText}
          onChange={(e) => setDraftText(e.target.value)}
        />

        {selectedLink && (
          <div style={{ marginTop: 8 }}>
            <span className={`rn-tag-pill rn-tag-${selectedLink.linkType.toLowerCase()}`}>
              {selectedLink.linkLabel}
            </span>
            <button type="button" onClick={clearLink} style={{ marginLeft: 8, border: 'none', background: 'none', color: '#6B7280', cursor: 'pointer', fontSize: 11 }}>✕ remove link</button>
          </div>
        )}

        {linkPicker === 'TRANSACTION' && (
          <div style={{ marginTop: 8, border: '1px solid #E5E7EB', borderRadius: 12, background: '#fff', padding: 14, maxHeight: 360, overflowY: 'auto' }}>
            <div className="csr-search-wrapper" style={{ marginBottom: 12 }}>
              <span className="csr-search-icon"><SearchIcon /></span>
              <input
                className="csr-form-input csr-search-input"
                type="text"
                placeholder="Search by address or deal name..."
                value={txnSearch}
                onChange={(e) => setTxnSearch(e.target.value)}
                autoFocus
              />
            </div>

            {transactionOptions === null ? (
              <p className="csr-list-empty">Loading transactions…</p>
            ) : filteredTxnOptions.length === 0 ? (
              <p className="csr-list-empty">{txnSearch ? 'No matching properties.' : 'No transactions found.'}</p>
            ) : (
              <>
                <p className="csr-list-count">{filteredTxnOptions.length} {filteredTxnOptions.length === 1 ? 'property' : 'properties'} found</p>
                {filteredTxnOptions.map((txn) => {
                  const addr = txn.propertyAddress || txn.transactionName || 'Unknown Address';
                  const cityState = [txn.propertyCity, txn.propertyState].filter(Boolean).join(', ');
                  const badge = STATUS_BADGE[txn.status] || { label: txn.status, cls: 'csr-badge-prospect' };
                  return (
                    <div
                      key={txn.id || txn.transactionId}
                      className="csr-list-card"
                      role="button"
                      tabIndex={0}
                      onClick={() => pickTransaction(txn)}
                      onKeyDown={(e) => e.key === 'Enter' && pickTransaction(txn)}
                    >
                      <div className="csr-list-card-icon">🏠</div>
                      <div className="csr-list-card-content">
                        <div className="csr-list-card-title">{addr}</div>
                        {cityState && <div className="csr-list-card-desc">{cityState}</div>}
                        {txn.transactionName && <div className="csr-list-card-meta">{txn.transactionName}</div>}
                      </div>
                      <span className={`csr-status-badge ${badge.cls}`}>{badge.label}</span>
                      <div className="csr-list-card-arrow">→</div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}

        {linkPicker === 'PROPERTY' && (
          <div style={{ marginTop: 8, border: '1px solid #E5E7EB', borderRadius: 12, background: '#fff', padding: 14, maxHeight: 360, overflowY: 'auto' }}>
            <div className="csr-search-wrapper" style={{ marginBottom: 12 }}>
              <span className="csr-search-icon"><SearchIcon /></span>
              <input
                className="csr-form-input csr-search-input"
                type="text"
                placeholder="Search your favorited properties..."
                value={propSearch}
                onChange={(e) => setPropSearch(e.target.value)}
                autoFocus
              />
            </div>

            {propertyOptions === null ? (
              <p className="csr-list-empty">Loading properties…</p>
            ) : filteredPropOptions.length === 0 ? (
              <p className="csr-list-empty">
                {propSearch ? 'No matching properties.' : 'No favorited properties yet — heart a property to link notes to it here.'}
              </p>
            ) : (
              <>
                <p className="csr-list-count">{filteredPropOptions.length} {filteredPropOptions.length === 1 ? 'property' : 'properties'} found</p>
                {filteredPropOptions.map((prop) => {
                  const address = getPropertyAddress(prop);
                  const location = getPropertyLocation(prop);
                  const price = getPropertyPrice(prop);
                  const details = getPropertyDetails(prop);
                  const meta = [price, details].filter(Boolean).join(' · ');
                  return (
                    <div
                      key={getPropertyId(prop) || prop.propertyId}
                      className="csr-list-card"
                      role="button"
                      tabIndex={0}
                      onClick={() => pickProperty(prop)}
                      onKeyDown={(e) => e.key === 'Enter' && pickProperty(prop)}
                    >
                      <div className="csr-list-card-icon">🏠</div>
                      <div className="csr-list-card-content">
                        <div className="csr-list-card-title">{address}</div>
                        {location && <div className="csr-list-card-desc">{location}</div>}
                        {meta && <div className="csr-list-card-meta">{meta}</div>}
                      </div>
                      <div className="csr-list-card-arrow">→</div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}

        {showReminderInput && (
          <div style={{ marginTop: 8 }}>
            <input
              type="date"
              value={draftReminder}
              onChange={(e) => setDraftReminder(e.target.value)}
              style={{ fontSize: 12, border: '1px solid #E5E7EB', borderRadius: 6, padding: '4px 8px' }}
            />
          </div>
        )}

        <div className="rn-top-composer-row">
          <div className="rn-composer-left">
            <button
              type="button"
              className={`rn-chip-btn${selectedLink?.linkType === 'TRANSACTION' ? ' rn-chip-active' : ''}`}
              onClick={() => openLinkPicker('TRANSACTION')}
            ><LinkIcon /> Link to Transaction</button>
            <button
              type="button"
              className={`rn-chip-btn${selectedLink?.linkType === 'PROPERTY' ? ' rn-chip-active' : ''}`}
              onClick={() => openLinkPicker('PROPERTY')}
            ><LinkIcon /> Link to Property</button>
            <button
              type="button"
              className={`rn-chip-btn${showReminderInput ? ' rn-chip-active' : ''}`}
              onClick={() => setShowReminderInput((v) => !v)}
            ><CalendarIcon /> Add reminder date</button>
          </div>
          <button type="button" className="rn-save-btn" disabled={!draftText.trim() || saving} onClick={handleSave}>
            {saving ? 'Saving…' : 'Save Note'}
          </button>
        </div>
      </div>

      <div className="rn-filter-bar">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`rn-fchip${activeFilter === f.key ? ' rn-active' : ''}`}
            onClick={() => setActiveFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="rn-empty-state"><div className="rn-es-sub">Loading notes…</div></div>
      ) : filteredNotes.length === 0 ? (
        <div className="rn-empty-state">
          <div className="rn-es-title">No notes yet</div>
          <div className="rn-es-sub">Notes you create here, or via the note icon anywhere in the app, will show up in this list.</div>
        </div>
      ) : (
        filteredNotes.map((note) => (
          <NoteCard key={note.id} note={note} variant="card" onUpdate={handleUpdate} onDelete={handleDelete} onNavigate={handleNoteClick} />
        ))
      )}
    </div>
  );
}
